import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { AlertTriangle, Bug, CheckCircle, Activity, Bell } from 'lucide-react';

/**
 * Admin → Observability tile (Iter 142.7)
 * ========================================
 * Lets ops verify the Sentry pipeline end-to-end without leaving the
 * admin panel:
 *   - "Trigger backend error" → hits /api/admin/observability/sentry-test
 *     which deliberately raises so a 500 surfaces in the Sentry inbox.
 *   - "Trigger frontend error" → throws inside the React tree so the
 *     SentryErrorBoundary captures + ships a JS-side event.
 *   - Live status pill — pings /api/admin/observability/sentry-status to
 *     show env, release, sample rate, last-event-id (best-effort).
 */
export default function SentryHealthPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forceCrash, setForceCrash] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/observability/sentry-status');
      setStatus(r.data);
    } catch (err) {
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const triggerBackend = async (kind) => {
    try {
      await api.post(`/api/admin/observability/sentry-test?kind=${kind}`);
      toast.success(`${kind} sent — check your Sentry inbox in ~30 s`);
    } catch (err) {
      // Expected for kind=exception (server-side raise yields 500); message
      // sends fine. Either way, the event was shipped.
      const code = err?.response?.status;
      if (code === 500 && kind === 'exception') {
        toast.success('Server-side exception fired — check Sentry inbox');
      } else if (code === 200) {
        toast.success('Test event sent — check Sentry inbox');
      } else {
        toast.error(`Backend test failed: ${code || 'network error'}`);
      }
    } finally {
      refresh();
    }
  };

  const triggerFrontend = (kind) => {
    if (kind === 'message') {
      // Non-fatal info-level event
      try {
        // Lazy-load Sentry from the page bundle so we use the SAME
        // initialized client (not a fresh import that would have no DSN).
        import('@sentry/react').then((Sentry) => {
          const id = Sentry.captureMessage(
            'Admin → Sentry Health: deliberate test message',
            'info'
          );
          toast.success(`Captured event id ${(id || '').slice(0, 8)}…`);
        });
      } catch (err) {
        toast.error(`Capture failed: ${err.message}`);
      }
    } else if (kind === 'exception') {
      // Force a render-tree crash so the SentryErrorBoundary catches it
      setForceCrash(true);
    }
  };

  // Component that crashes when forceCrash is true — sibling to the page,
  // wrapped so only this tile's section blanks out.
  const CrashOnDemand = () => {
    if (forceCrash) {
      throw new Error('Admin → Sentry Health: deliberate test exception');
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="sentry-health-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Observability — Sentry Health</h1>
        <p className="text-sm text-gray-400">
          Verify error tracking is wired correctly across backend + frontend. Use
          before launches to confirm events are flowing into your Sentry inbox.
        </p>
      </div>

      {/* Live status panel */}
      <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" /> Backend Sentry status
          </h2>
          <button
            onClick={refresh}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"
            data-testid="sentry-refresh-btn"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {!status && <p className="text-sm text-gray-500">Loading…</p>}
        {status && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Status" value={status.enabled ? 'ENABLED' : 'DISABLED'} accent={status.enabled ? 'green' : 'red'} testid="sentry-status-enabled" />
            <Stat label="Environment" value={status.environment || '—'} testid="sentry-status-env" />
            <Stat label="Release" value={(status.release || '—').slice(0, 28)} testid="sentry-status-release" />
            <Stat label="Traces sample rate" value={status.traces_sample_rate ?? '—'} testid="sentry-status-traces" />
            <Stat label="Profiles sample rate" value={status.profiles_sample_rate ?? '—'} testid="sentry-status-profiles" />
            <Stat label="DSN" value={status.dsn_short || '—'} testid="sentry-status-dsn" />
          </div>
        )}
        {status && !status.enabled && (
          <div className="flex items-start gap-2 rounded-lg p-3 text-sm text-amber-300"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>SENTRY_DSN env var is not set on this backend instance.</span>
          </div>
        )}
      </div>

      {/* Test triggers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Bug className="w-4 h-4 text-red-400" /> Backend triggers
          </h3>
          <button
            onClick={() => triggerBackend('message')}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-emerald-200 hover:bg-emerald-500/15 transition"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}
            data-testid="trigger-backend-message"
          >
            Send info-level message
          </button>
          <button
            onClick={() => triggerBackend('exception')}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-red-200 hover:bg-red-500/15 transition"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
            data-testid="trigger-backend-exception"
          >
            Raise ZeroDivisionError (returns 500)
          </button>
        </div>

        <div className="rounded-2xl p-5 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Bell className="w-4 h-4 text-yellow-400" /> Frontend triggers
          </h3>
          <button
            onClick={() => triggerFrontend('message')}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-emerald-200 hover:bg-emerald-500/15 transition"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}
            data-testid="trigger-frontend-message"
          >
            Send info-level message
          </button>
          <button
            onClick={() => triggerFrontend('exception')}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-red-200 hover:bg-red-500/15 transition"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
            data-testid="trigger-frontend-exception"
          >
            Throw render-tree exception
          </button>
          <CrashOnDemand />
        </div>
      </div>

      {/* What happens next */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.18)' }}>
        <h3 className="text-sm font-semibold text-amber-200 mb-2 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> After triggering a test
        </h3>
        <ul className="text-xs text-gray-300 space-y-1 list-disc pl-5">
          <li>Open your Sentry org dashboard → Issues — events arrive within 5-30 s.</li>
          <li>Each event has the player's user_id attached, plus environment + release tags so you can filter <code>environment:production</code> separately.</li>
          <li>Frontend errors include a session replay (errors-only) you can scrub through to see exactly what the player did.</li>
          <li>5xx backend errors include the FastAPI route, request body (PII-scrubbed), correlation_id, and a full stack.</li>
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const colour = accent === 'green' ? '#22c55e' : accent === 'red' ? '#ef4444' : '#fff';
  return (
    <div className="rounded-lg px-3 py-2.5"
      style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}
      data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm font-semibold mt-0.5" style={{ color: colour }}>{String(value)}</p>
    </div>
  );
}
